import React, { useState, useEffect } from 'react';
import {
    Paper, Typography, Grid, TextField, Button,
    Card, CardContent, CardHeader, IconButton,
    Table, TableBody, TableCell, TableHead, TableRow,
    Divider, Box,
    Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions
} from '@material-ui/core';
import { Edit as EditIcon, Save as SaveIcon, Close as CloseIcon } from '@material-ui/icons';
import { makeStyles } from '@material-ui/core/styles';
import HttpService from '../../services/httpService';

const useStyles = makeStyles((theme) => ({
    root: {
        padding: '24px',
        background: '#f5f5f5',
        minHeight: '100vh'
    },
    mainCard: {
        borderRadius: '16px',
        boxShadow: '0 4px 20px rgba(0,0,0,0.08)'
    },
    header: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '24px',
        paddingBottom: '16px',
        borderBottom: `3px solid #fdb913`
    },
    title: {
        fontWeight: 700,
        color: '#1c375d',
        letterSpacing: '0.5px'
    },
    configCard: {
        borderRadius: '12px',
        borderTop: `4px solid #fdb913`,
        transition: 'transform 0.2s',
        '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: '0 6px 16px rgba(0,0,0,0.1)'
        }
    },
    cardHeader: {
        backgroundColor: '#1c375d',
        color: '#fff',
        borderTopLeftRadius: '8px',
        borderTopRightRadius: '8px',
        '& .MuiCardHeader-title': {
            fontSize: '16px',
            fontWeight: 600
        }
    },
    cardHeaderAction: {
        display: 'flex',
        alignItems: 'center',
        gap: '4px',
        marginTop: '0 !important',
        marginRight: '-4px'
    },
    cardActionBtn: {
        color: '#fff',
        padding: '6px',
        transition: 'all 0.2s ease',
        '&:hover': {
            backgroundColor: 'rgba(255,255,255,0.2)',
            transform: 'scale(1.1)'
        }
    },
    saveActionBtn: {
        color: '#4caf50',
        backgroundColor: 'rgba(76, 175, 80, 0.15)',
        padding: '6px',
        transition: 'all 0.2s ease',
        '&:hover': {
            backgroundColor: 'rgba(76, 175, 80, 0.35)',
            transform: 'scale(1.1)'
        }
    },
    cancelActionBtn: {
        color: '#ff7043',
        padding: '6px',
        transition: 'all 0.2s ease',
        '&:hover': {
            backgroundColor: 'rgba(255,112,67,0.2)',
            transform: 'scale(1.1)'
        }
    },
    saveButton: {
        backgroundColor: '#4caf50',
        color: '#fff',
        '&:hover': {
            backgroundColor: '#45a049'
        }
    },
    editButton: {
        backgroundColor: '#fdb913',
        color: '#1c375d',
        '&:hover': {
            backgroundColor: '#e6a811'
        }
    },
    tableHeader: {
        backgroundColor: '#1c375d',
        '& th': {
            color: '#fff',
            fontWeight: 600,
            fontSize: '13px'
        }
    },
    tierRow: {
        '&:nth-of-type(even)': {
            backgroundColor: '#f9f9f9'
        }
    },
    fieldLabel: {
        '& .MuiInputLabel-root': {
            color: '#1c375d',
            fontWeight: 700,
            fontSize: '13px',
        },
        '& .MuiInputLabel-root.Mui-disabled': {
            color: '#1c375d',
            fontWeight: 700,
        },
        '& .MuiInputLabel-shrink': {
            color: '#1c375d',
            fontWeight: 700,
        },
        '& .MuiInput-underline:before': {
            borderBottomColor: '#ddd',
        },
        '& .MuiInput-underline.Mui-disabled:before': {
            borderBottomStyle: 'dotted',
            borderBottomColor: '#ccc',
        },
        '& .MuiFormHelperText-root': {
            color: '#888',
            fontSize: '11px',
        },
        '& .MuiInputBase-input': {
            color: '#222',
            fontWeight: 500,
            fontSize: '15px',
        },
        '& .MuiInputBase-input.Mui-disabled': {
            color: '#333',
            WebkitTextFillColor: '#333',
        },
    },
    confirmDialog: {
        '& .MuiDialog-paper': {
            borderRadius: '16px',
            padding: '8px',
            minWidth: '380px'
        }
    },
    confirmDialogTitle: {
        color: '#1c375d',
        fontWeight: 700,
        fontSize: '20px',
        paddingBottom: '0'
    },
    confirmDialogContent: {
        color: '#555',
        fontSize: '15px',
        paddingTop: '8px'
    },
    confirmYesBtn: {
        backgroundColor: '#4caf50',
        color: '#fff',
        borderRadius: '8px',
        fontWeight: 600,
        padding: '8px 28px',
        textTransform: 'none',
        '&:hover': {
            backgroundColor: '#43a047'
        }
    },
    confirmNoBtn: {
        backgroundColor: '#f5f5f5',
        color: '#1c375d',
        borderRadius: '8px',
        fontWeight: 600,
        padding: '8px 28px',
        textTransform: 'none',
        border: '1px solid #ddd',
        '&:hover': {
            backgroundColor: '#eee'
        }
    }
}));

// Card keys for per-card editing
const CARD_KEYS = {
    REVENUE: 'revenue',
    NPS: 'nps',
    TENURE: 'tenure',
    POST_NPS: 'postNps',
    EKOM: 'ekom',
    REDEMPTION: 'redemption',
    TIERS: 'tiers',
    GIFTS: 'gifts'
};

const RewardConfig = () => {
    const classes = useStyles();
    const [config, setConfig] = useState(null);
    const [editingCard, setEditingCard] = useState(null); // which card is in edit mode
    const [tempConfig, setTempConfig] = useState(null);
    const [loading, setLoading] = useState(false);
    const [confirmSaveCard, setConfirmSaveCard] = useState(null); // which card triggered save confirmation

    useEffect(() => {
        fetchConfig();
    }, []);

    const fetchConfig = async () => {
        setLoading(true);
        try {
            const response = await HttpService.getRewardConfig();
            if (response?.data?.success) {
                setConfig(response.data.data);
                setTempConfig(response.data.data);
            }
        } catch (error) {
            console.error('Error fetching config:', error);
        }
        setLoading(false);
    };

    const isEditing = (cardKey) => editingCard === cardKey;

    const handleEditCard = (cardKey) => {
        // Reset tempConfig to current saved config before entering edit mode
        setTempConfig(JSON.parse(JSON.stringify(config)));
        setEditingCard(cardKey);
    };

    const handleCancelEdit = () => {
        // Revert tempConfig back to saved config
        setTempConfig(JSON.parse(JSON.stringify(config)));
        setEditingCard(null);
    };

    const handleSaveClick = (cardKey) => {
        // Open confirmation modal
        setConfirmSaveCard(cardKey);
    };

    const handleConfirmSave = async () => {
        setLoading(true);
        try {
            // Remove _id and __v from config before sending
            const { _id, __v, ...cleanConfig } = tempConfig;

            const response = await HttpService.updateRewardConfig({ config: cleanConfig });
            if (response?.data?.success) {
                setConfig(JSON.parse(JSON.stringify(tempConfig)));
                setEditingCard(null);
                setConfirmSaveCard(null);
                alert('Configuration updated successfully!');
            }
        } catch (error) {
            console.error('Error saving config:', error);
            alert('Error saving configuration');
        }
        setLoading(false);
    };

    const handleCancelSave = () => {
        setConfirmSaveCard(null);
    };

    // Helper to render card header action buttons (edit / save+cancel)
    const renderCardActions = (cardKey) => {
        if (isEditing(cardKey)) {
            return (
                <div className={classes.cardHeaderAction}>
                    <IconButton
                        className={classes.saveActionBtn}
                        onClick={() => handleSaveClick(cardKey)}
                        title="Save"
                        size="small"
                    >
                        <SaveIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                        className={classes.cancelActionBtn}
                        onClick={handleCancelEdit}
                        title="Cancel"
                        size="small"
                    >
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </div>
            );
        }
        return (
            <IconButton
                className={classes.cardActionBtn}
                onClick={() => handleEditCard(cardKey)}
                title="Edit"
                size="small"
                disabled={editingCard !== null && editingCard !== cardKey}
            >
                <EditIcon fontSize="small" />
            </IconButton>
        );
    };

    if (!config || !tempConfig) {
        return (
            <div className={classes.root}>
                <Typography style={{ textAlign: 'center', marginTop: '50px' }}>Loading configuration...</Typography>
            </div>
        );
    }

    return (
        <div className={classes.root}>
            <Paper className={classes.mainCard} style={{ padding: '28px' }}>
                {/* Header */}
                <div className={classes.header}>
                    <Typography variant="h4" className={classes.title}>
                        ⚙️ Reward Points Configuration
                    </Typography>
                </div>

                {/* Row 1 — 3 equal cards */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '20px', marginBottom: '20px' }}>

                    {/* Revenue Points Card */}
                    <Card className={classes.configCard}>
                        <CardHeader title="💰 Revenue Achievement" className={classes.cardHeader} action={renderCardActions(CARD_KEYS.REVENUE)} />
                        <CardContent style={{ padding: '20px 24px' }}>
                            <TextField
                                fullWidth className={classes.fieldLabel}
                                label="Multiplier (Rs.)" type="number"
                                value={tempConfig.revenuePoints.multiplier}
                                disabled={!isEditing(CARD_KEYS.REVENUE)}
                                onChange={(e) => setTempConfig({ ...tempConfig, revenuePoints: { ...tempConfig.revenuePoints, multiplier: parseInt(e.target.value) } })}
                                helperText="Every X rupees = 1 point"
                                InputProps={{ style: { fontSize: '14px' } }}
                            />
                        </CardContent>
                    </Card>

                    {/* NPS Points Card */}
                    <Card className={classes.configCard}>
                        <CardHeader title="⭐ NPS Points" className={classes.cardHeader} action={renderCardActions(CARD_KEYS.NPS)} />
                        <CardContent style={{ padding: '20px 24px' }}>
                            <TextField
                                fullWidth className={classes.fieldLabel}
                                label="Required NPS Score" type="number"
                                value={tempConfig.npsPoints.requiredScore}
                                disabled={!isEditing(CARD_KEYS.NPS)}
                                onChange={(e) => setTempConfig({ ...tempConfig, npsPoints: { ...tempConfig.npsPoints, requiredScore: parseInt(e.target.value) } })}
                                style={{ marginBottom: '20px' }}
                                InputProps={{ style: { fontSize: '14px' } }}
                            />
                            <TextField
                                fullWidth className={classes.fieldLabel}
                                label={`Points per Project (NPS = ${tempConfig.npsPoints.requiredScore})`} type="number"
                                value={tempConfig.npsPoints.pointsPerProject}
                                disabled={!isEditing(CARD_KEYS.NPS)}
                                onChange={(e) => setTempConfig({ ...tempConfig, npsPoints: { ...tempConfig.npsPoints, pointsPerProject: parseInt(e.target.value) } })}
                                InputProps={{ style: { fontSize: '14px' } }}
                            />
                        </CardContent>
                    </Card>

                    {/* Tenure Points Card */}
                    <Card className={classes.configCard}>
                        <CardHeader title="📅 Tenure Points" className={classes.cardHeader} action={renderCardActions(CARD_KEYS.TENURE)} />
                        <CardContent style={{ padding: '20px 24px' }}>
                            <TextField
                                fullWidth className={classes.fieldLabel}
                                label="Minimum Months Required" type="number"
                                value={tempConfig.tenurePoints.minMonths}
                                disabled={!isEditing(CARD_KEYS.TENURE)}
                                onChange={(e) => setTempConfig({ ...tempConfig, tenurePoints: { ...tempConfig.tenurePoints, minMonths: parseInt(e.target.value) } })}
                                style={{ marginBottom: '20px' }}
                                InputProps={{ style: { fontSize: '14px' } }}
                            />
                            <TextField
                                fullWidth className={classes.fieldLabel}
                                label="Points per Completed Year" type="number"
                                value={tempConfig.tenurePoints.pointsPerYear}
                                disabled={!isEditing(CARD_KEYS.TENURE)}
                                onChange={(e) => setTempConfig({ ...tempConfig, tenurePoints: { ...tempConfig.tenurePoints, pointsPerYear: parseInt(e.target.value) } })}
                                InputProps={{ style: { fontSize: '14px' } }}
                            />
                        </CardContent>
                    </Card>

                </div>

                {/* Row 2 — Redemption (wider) + Post NPS + EKOM */}
                <div style={{ display: 'grid', gridTemplateColumns: '1.6fr 1fr 1fr', gap: '20px', marginBottom: '20px' }}>

                    {/* Redemption Card */}
                    <Card className={classes.configCard}>
                        <CardHeader title="💎 Redemption Settings" className={classes.cardHeader} action={renderCardActions(CARD_KEYS.REDEMPTION)} />
                        <CardContent style={{ padding: '20px 24px' }}>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 24px' }}>
                                <TextField
                                    fullWidth className={classes.fieldLabel}
                                    label="Min Achievement % (Monthly)" type="number"
                                    value={tempConfig.redemption?.minAchievementPct ?? 80}
                                    disabled={!isEditing(CARD_KEYS.REDEMPTION)}
                                    onChange={(e) => setTempConfig({ ...tempConfig, redemption: { ...tempConfig.redemption, minAchievementPct: parseInt(e.target.value) } })}
                                    style={{ marginBottom: '20px' }}
                                    helperText="Monthly eligible threshold"
                                    InputProps={{ style: { fontSize: '14px' } }}
                                />
                                <TextField
                                    fullWidth className={classes.fieldLabel}
                                    label="Min Consecutive Months" type="number"
                                    value={tempConfig.redemption?.minConsecutiveMonths ?? 3}
                                    disabled={!isEditing(CARD_KEYS.REDEMPTION)}
                                    onChange={(e) => setTempConfig({ ...tempConfig, redemption: { ...tempConfig.redemption, minConsecutiveMonths: parseInt(e.target.value) } })}
                                    style={{ marginBottom: '20px' }}
                                    helperText={`${tempConfig.redemption?.minConsecutiveMonths ?? 3} months ≥ ${tempConfig.redemption?.minAchievementPct ?? 80}% needed`}
                                    InputProps={{ style: { fontSize: '14px' } }}
                                />
                                <TextField
                                    fullWidth className={classes.fieldLabel}
                                    label="Year-End Min Achievement %" type="number"
                                    value={tempConfig.redemption?.yearEndMinAchievementPct ?? 70}
                                    disabled={!isEditing(CARD_KEYS.REDEMPTION)}
                                    onChange={(e) => setTempConfig({ ...tempConfig, redemption: { ...tempConfig.redemption, yearEndMinAchievementPct: parseInt(e.target.value) } })}
                                    helperText="Cumulative % at year-end"
                                    InputProps={{ style: { fontSize: '14px' } }}
                                />
                                <TextField
                                    fullWidth className={classes.fieldLabel}
                                    label="Points per Rupee" type="number"
                                    value={tempConfig.redemption?.pointsPerRupee ?? 4}
                                    disabled={!isEditing(CARD_KEYS.REDEMPTION)}
                                    onChange={(e) => setTempConfig({ ...tempConfig, redemption: { ...tempConfig.redemption, pointsPerRupee: parseInt(e.target.value) } })}
                                    helperText="Example: 4 points = ₹1"
                                    InputProps={{ style: { fontSize: '14px' } }}
                                />
                            </div>
                        </CardContent>
                    </Card>

                    {/* Post Design NPS Card */}
                    <Card className={classes.configCard}>
                        <CardHeader title="🎨 Post Design NPS" className={classes.cardHeader} action={renderCardActions(CARD_KEYS.POST_NPS)} />
                        <CardContent style={{ padding: '20px 24px' }}>
                            <TextField
                                fullWidth className={classes.fieldLabel}
                                label="Required Score" type="number"
                                inputProps={{ step: 0.1, min: 0, max: 5 }}
                                value={tempConfig.postDesignNPS?.requiredScore ?? 4.5}
                                disabled={!isEditing(CARD_KEYS.POST_NPS)}
                                onChange={(e) => setTempConfig({ ...tempConfig, postDesignNPS: { ...tempConfig.postDesignNPS, requiredScore: parseFloat(e.target.value) } })}
                                style={{ marginBottom: '20px' }}
                                helperText="Minimum score to earn points"
                                InputProps={{ style: { fontSize: '14px' } }}
                            />
                            <TextField
                                fullWidth className={classes.fieldLabel}
                                label={`Points (Score ≥ ${tempConfig.postDesignNPS?.requiredScore ?? 4.5})`} type="number"
                                value={tempConfig.postDesignNPS?.points ?? 25000}
                                disabled={!isEditing(CARD_KEYS.POST_NPS)}
                                onChange={(e) => setTempConfig({ ...tempConfig, postDesignNPS: { ...tempConfig.postDesignNPS, points: parseInt(e.target.value) } })}
                                InputProps={{ style: { fontSize: '14px' } }}
                            />
                        </CardContent>
                    </Card>

                    {/* EKOM Points Card */}
                    <Card className={classes.configCard}>
                        <CardHeader title="⚡ EKOM Points" className={classes.cardHeader} action={renderCardActions(CARD_KEYS.EKOM)} />
                        <CardContent style={{ padding: '20px 24px' }}>
                            <TextField
                                fullWidth className={classes.fieldLabel}
                                label="Max Days (EKOM to DSO)" type="number"
                                value={tempConfig.ekomPoints?.maxDays ?? 30}
                                disabled={!isEditing(CARD_KEYS.EKOM)}
                                onChange={(e) => setTempConfig({ ...tempConfig, ekomPoints: { ...tempConfig.ekomPoints, maxDays: parseInt(e.target.value) } })}
                                style={{ marginBottom: '20px' }}
                                helperText={`Within ${tempConfig.ekomPoints?.maxDays ?? 30} days`}
                                InputProps={{ style: { fontSize: '14px' } }}
                            />
                            <TextField
                                fullWidth className={classes.fieldLabel}
                                label="Points per EKOM Project" type="number"
                                value={tempConfig.ekomPoints?.pointsPerProject ?? 1000}
                                disabled={!isEditing(CARD_KEYS.EKOM)}
                                onChange={(e) => setTempConfig({
                                    ...tempConfig,
                                    ekomPoints: { ...tempConfig.ekomPoints, pointsPerProject: parseInt(e.target.value) }
                                    })}
                                    InputProps={{ style: { fontSize: '14px' } }}
                                />
                            </CardContent>
                        </Card>

                </div>{/* END ROW 2 */}

                {/* Redemption Tiers — Full Width */}
                <div style={{ marginTop: '4px' }}>
                        <Card className={classes.configCard}>
                            <CardHeader 
                                title="🏆 Redemption Tiers" 
                                className={classes.cardHeader}
                                subheader="Based on cumulative achievement percentage"
                                subheaderTypographyProps={{ style: { color: '#ffd54f', fontSize: '12px' } }}
                                action={renderCardActions(CARD_KEYS.TIERS)}
                            />
                            <CardContent style={{ padding: '20px 24px' }}>
                                <Table size="small">
                                    <TableHead className={classes.tableHeader}>
                                        <TableRow>
                                            <TableCell>Min %</TableCell>
                                            <TableCell>Max %</TableCell>
                                            <TableCell>Redeem %</TableCell>
                                            {isEditing(CARD_KEYS.TIERS) && <TableCell align="center">Action</TableCell>}
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {tempConfig.redemptionTiers.map((tier, index) => (
                                            <TableRow key={index} className={classes.tierRow}>
                                                <TableCell>
                                                    {isEditing(CARD_KEYS.TIERS) ? (
                                                        <TextField
                                                            type="number"
                                                            value={tier.minPoints > 100 ? Math.floor(tier.minPoints / 1000) : tier.minPoints}
                                                            onChange={(e) => {
                                                                const newTiers = [...tempConfig.redemptionTiers];
                                                                newTiers[index].minPoints = parseInt(e.target.value);
                                                                setTempConfig({ ...tempConfig, redemptionTiers: newTiers });
                                                            }}
                                                            size="small"
                                                            inputProps={{ min: 0, max: 100 }}
                                                            InputProps={{ style: { fontSize: '12px', width: '100px' }, endAdornment: <span style={{fontSize:'11px'}}>%</span> }}
                                                        />
                                                    ) : (
                                                        <Typography style={{ fontWeight: 500 }}>{tier.minPoints > 100 ? Math.floor(tier.minPoints / 1000) : tier.minPoints}%</Typography>
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    {isEditing(CARD_KEYS.TIERS) ? (
                                                        <TextField
                                                            type="number"
                                                            value={tier.maxPoints ? (tier.maxPoints > 100 ? Math.floor(tier.maxPoints / 1000) : tier.maxPoints) : ''}
                                                            onChange={(e) => {
                                                                const newTiers = [...tempConfig.redemptionTiers];
                                                                newTiers[index].maxPoints = e.target.value ? parseInt(e.target.value) : null;
                                                                setTempConfig({ ...tempConfig, redemptionTiers: newTiers });
                                                            }}
                                                            size="small"
                                                            inputProps={{ min: 0, max: 100 }}
                                                            InputProps={{ style: { fontSize: '12px', width: '100px' }, endAdornment: <span style={{fontSize:'11px'}}>%</span> }}
                                                        />
                                                    ) : (
                                                        <Typography style={{ fontWeight: 500 }}>
                                                            {tier.maxPoints ? `${tier.maxPoints > 100 ? Math.floor(tier.maxPoints / 1000) : tier.maxPoints}%` : '∞'}
                                                        </Typography>
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    {isEditing(CARD_KEYS.TIERS) ? (
                                                        <TextField
                                                            type="number"
                                                            value={tier.redeemPercentage * 100}
                                                            onChange={(e) => {
                                                                const newTiers = [...tempConfig.redemptionTiers];
                                                                newTiers[index].redeemPercentage = parseInt(e.target.value) / 100;
                                                                setTempConfig({ ...tempConfig, redemptionTiers: newTiers });
                                                            }}
                                                            size="small"
                                                            InputProps={{ style: { fontSize: '12px', width: '80px' } }}
                                                        />
                                                    ) : (
                                                        <Typography style={{ fontWeight: 600, color: tier.redeemPercentage > 0 ? '#4caf50' : '#999' }}>
                                                            {tier.redeemPercentage * 100}%
                                                        </Typography>
                                                    )}
                                                </TableCell>
                                                {isEditing(CARD_KEYS.TIERS) && (
                                                    <TableCell align="center">
                                                        <IconButton 
                                                            size="small"
                                                            onClick={() => {
                                                                const newTiers = [...tempConfig.redemptionTiers];
                                                                newTiers.splice(index, 1);
                                                                setTempConfig({ ...tempConfig, redemptionTiers: newTiers });
                                                            }}
                                                            style={{ color: '#f44336' }}
                                                        >
                                                            🗑️
                                                        </IconButton>
                                                    </TableCell>
                                                )}
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                                {isEditing(CARD_KEYS.TIERS) && (
                                    <Button
                                        size="small"
                                        style={{ marginTop: '16px', color: '#4caf50' }}
                                        onClick={() => {
                                            setTempConfig({
                                                ...tempConfig,
                                                redemptionTiers: [
                                                    ...tempConfig.redemptionTiers,
                                                    { minPoints: 0, maxPoints: null, redeemPercentage: 0 }
                                                ]
                                            });
                                        }}
                                    >
                                        + Add Tier
                                    </Button>
                                )}
                            </CardContent>
                        </Card>
                </div>

                {/* Gift Items — Full Width */}
                <div style={{ marginTop: '24px' }}>
                        <Card className={classes.configCard}>
                            <CardHeader 
                                title="🎁 Gift Items Mapping" 
                                className={classes.cardHeader}
                                subheader="Points to Gift Items conversion"
                                subheaderTypographyProps={{ style: { color: '#ffd54f', fontSize: '12px' } }}
                                action={renderCardActions(CARD_KEYS.GIFTS)}
                            />
                            <CardContent style={{ padding: '20px 24px' }}>
                                <Table size="small">
                                    <TableHead className={classes.tableHeader}>
                                        <TableRow>
                                            <TableCell>Min Points</TableCell>
                                            <TableCell>Max Points</TableCell>
                                            <TableCell>Amount (₹)</TableCell>
                                            <TableCell>Gift Items</TableCell>
                                            {isEditing(CARD_KEYS.GIFTS) && <TableCell align="center">Action</TableCell>}
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {tempConfig.giftItems?.map((gift, index) => (
                                            <TableRow key={index} className={classes.tierRow}>
                                                <TableCell>
                                                    {isEditing(CARD_KEYS.GIFTS) ? (
                                                        <TextField
                                                            type="number"
                                                            value={gift.minPoints}
                                                            onChange={(e) => {
                                                                const newGifts = [...tempConfig.giftItems];
                                                                newGifts[index].minPoints = parseInt(e.target.value);
                                                                setTempConfig({ ...tempConfig, giftItems: newGifts });
                                                            }}
                                                            size="small"
                                                            InputProps={{ style: { fontSize: '12px', width: '100px' } }}
                                                        />
                                                    ) : (
                                                        <Typography style={{ fontWeight: 500 }}>{gift.minPoints.toLocaleString()}</Typography>
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    {isEditing(CARD_KEYS.GIFTS) ? (
                                                        <TextField
                                                            type="number"
                                                            value={gift.maxPoints || ''}
                                                            onChange={(e) => {
                                                                const newGifts = [...tempConfig.giftItems];
                                                                newGifts[index].maxPoints = e.target.value ? parseInt(e.target.value) : null;
                                                                setTempConfig({ ...tempConfig, giftItems: newGifts });
                                                            }}
                                                            size="small"
                                                            InputProps={{ style: { fontSize: '12px', width: '100px' } }}
                                                        />
                                                    ) : (
                                                        <Typography style={{ fontWeight: 500 }}>
                                                            {gift.maxPoints ? gift.maxPoints.toLocaleString() : '∞'}
                                                        </Typography>
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    {isEditing(CARD_KEYS.GIFTS) ? (
                                                        <TextField
                                                            type="number"
                                                            value={gift.amount}
                                                            onChange={(e) => {
                                                                const newGifts = [...tempConfig.giftItems];
                                                                newGifts[index].amount = parseInt(e.target.value);
                                                                setTempConfig({ ...tempConfig, giftItems: newGifts });
                                                            }}
                                                            size="small"
                                                            InputProps={{ style: { fontSize: '12px', width: '100px' } }}
                                                        />
                                                    ) : (
                                                        <Typography style={{ fontWeight: 600, color: '#4caf50' }}>
                                                            ₹{gift.amount.toLocaleString()}
                                                        </Typography>
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    {isEditing(CARD_KEYS.GIFTS) ? (
                                                        <TextField
                                                            fullWidth
                                                            value={gift.items?.join(', ') || ''}
                                                            onChange={(e) => {
                                                                const newGifts = [...tempConfig.giftItems];
                                                                newGifts[index].items = e.target.value.split(',').map(item => item.trim());
                                                                setTempConfig({ ...tempConfig, giftItems: newGifts });
                                                            }}
                                                            size="small"
                                                            placeholder="Separate items with comma"
                                                            InputProps={{ style: { fontSize: '12px' } }}
                                                        />
                                                    ) : (
                                                        <Typography style={{ fontSize: '12px' }}>
                                                            {gift.items?.join(' • ') || '-'}
                                                        </Typography>
                                                    )}
                                                </TableCell>
                                                {isEditing(CARD_KEYS.GIFTS) && (
                                                    <TableCell align="center">
                                                        <IconButton 
                                                            size="small"
                                                            onClick={() => {
                                                                const newGifts = [...tempConfig.giftItems];
                                                                newGifts.splice(index, 1);
                                                                setTempConfig({ ...tempConfig, giftItems: newGifts });
                                                            }}
                                                            style={{ color: '#f44336' }}
                                                        >
                                                            🗑️
                                                        </IconButton>
                                                    </TableCell>
                                                )}
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                                {isEditing(CARD_KEYS.GIFTS) && (
                                    <Button
                                        size="small"
                                        style={{ marginTop: '16px', color: '#4caf50' }}
                                        onClick={() => {
                                            setTempConfig({
                                                ...tempConfig,
                                                giftItems: [
                                                    ...(tempConfig.giftItems || []),
                                                    { minPoints: 0, maxPoints: null, amount: 0, items: [] }
                                                ]
                                            });
                                        }}
                                    >
                                        + Add Gift Item
                                    </Button>
                                )}
                            </CardContent>
                        </Card>
                </div>

            </Paper>

            {/* Confirmation Modal */}
            <Dialog
                open={confirmSaveCard !== null}
                onClose={handleCancelSave}
                className={classes.confirmDialog}
                aria-labelledby="confirm-save-dialog-title"
            >
                <DialogTitle id="confirm-save-dialog-title" className={classes.confirmDialogTitle}>
                    Confirm Save
                </DialogTitle>
                <DialogContent>
                    <DialogContentText className={classes.confirmDialogContent}>
                        Are you sure you want to save the changes?
                    </DialogContentText>
                </DialogContent>
                <DialogActions style={{ padding: '16px 24px', gap: '12px' }}>
                    <Button onClick={handleCancelSave} className={classes.confirmNoBtn}>
                        No
                    </Button>
                    <Button onClick={handleConfirmSave} className={classes.confirmYesBtn} autoFocus disabled={loading}>
                        {loading ? 'Saving...' : 'Yes'}
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
};

export default RewardConfig;